/**
 * AdminIncidentDetailPage — [UC-025]
 * Admin categorizes an Open incident as Bug or ChangeRequest with reasoning.
 * On submit the backend closes the incident, optionally spawns a change
 * request, logs the action, and emails the project owner (non-blocking).
 * After success we navigate back to the originating admin project detail.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bug, GitPullRequest, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';

interface IncidentDetail {
  incidentId: number;
  projectId: number;
  description: string;
  status: string;
  creationDate: string;
}

export function AdminIncidentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [incident, setIncident] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'Bug' | 'ChangeRequest' | null>(null);
  const [reasoning, setReasoning] = useState('');

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true);
        const response = await api.get<IncidentDetail>(`/incidents/${id}`);
        setIncident(response);
      } catch (err) {
        console.error('Failed to load incident:', err);
        toast.error(t('adminIncidentDetail.loadError', 'Failed to load incident'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  const backToProject = () => {
    if (incident?.projectId) {
      navigate(`/admin/projects/${incident.projectId}`);
    } else {
      navigate('/admin/project-list');
    }
  };

  const handleSubmit = async () => {
    if (!incident || !selectedCategory || !reasoning.trim()) {
      toast.error(t('adminIncidentDetail.validation', 'Select a category and provide reasoning'));
      return;
    }
    try {
      setSubmitting(true);
      await api.patch(`/incidents/${incident.incidentId}/categorize`, {
        categorizationType: selectedCategory,
        reasoning: reasoning.trim(),
      });
      toast.success(
        selectedCategory === 'Bug'
          ? t('adminIncidentDetail.successBug', 'Incident categorized as Bug')
          : t('adminIncidentDetail.successChangeRequest', 'Incident categorized as Change Request'),
      );
      backToProject();
    } catch (err) {
      console.error('Failed to categorize incident:', err);
      toast.error(t('adminIncidentDetail.submitError', 'Failed to categorize incident'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-red-600/15 border border-red-600/25 rounded-xl p-4">
          <p className="text-red-700 text-sm">{t('adminIncidentDetail.notFound', 'Incident not found')}</p>
          <button onClick={() => navigate('/admin/project-list')} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700">
            {t('adminIncidentDetail.backToProjects', 'Back to projects')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <button onClick={backToProject}
          className="inline-flex items-center text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('adminIncidentDetail.backToProject', 'Back to project')}
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {t('adminIncidentDetail.title', 'Categorize Incident')} #{incident.incidentId}
        </h1>
      </div>

      <div className="mb-6 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-5">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
          {t('adminIncidentDetail.detailsHeading', 'Incident details')}
        </h2>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-[var(--text-muted)] mb-1">
              {t('adminIncidentDetail.description', 'Description')}
            </div>
            <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap break-words">
              {incident.description}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[var(--border-subtle)]">
            <span className="text-sm text-[var(--text-secondary)]">{t('adminIncidentDetail.status', 'Status')}</span>
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-600/15 text-red-700 border border-red-600/25">
              {incident.status}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[var(--text-secondary)]">{t('adminIncidentDetail.reported', 'Reported')}</span>
            <span className="text-sm text-[var(--text-primary)]">
              {new Date(incident.creationDate).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] shadow-sm p-5">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
          {t('adminIncidentDetail.categorization', 'Categorization')}
        </h2>

        <div className="mb-6">
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">
            {t('adminIncidentDetail.selectCategory', 'Select category')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => setSelectedCategory('Bug')}
              className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                selectedCategory === 'Bug'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-[var(--border-subtle)] hover:border-red-300 dark:hover:border-red-700'
              }`}>
              <div className="flex items-center gap-3">
                <Bug className={`h-5 w-5 ${selectedCategory === 'Bug' ? 'text-red-600' : 'text-[var(--text-secondary)]'}`} />
                <div>
                  <div className={`font-medium ${selectedCategory === 'Bug' ? 'text-red-700 dark:text-red-400' : 'text-[var(--text-primary)]'}`}>
                    {t('adminIncidentDetail.bug', 'Bug')}
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">
                    {t('adminIncidentDetail.bugDesc', 'Technical issue or defect')}
                  </div>
                </div>
              </div>
            </button>

            <button type="button" onClick={() => setSelectedCategory('ChangeRequest')}
              className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                selectedCategory === 'ChangeRequest'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-[var(--border-subtle)] hover:border-blue-300 dark:hover:border-blue-700'
              }`}>
              <div className="flex items-center gap-3">
                <GitPullRequest className={`h-5 w-5 ${selectedCategory === 'ChangeRequest' ? 'text-blue-600' : 'text-[var(--text-secondary)]'}`} />
                <div>
                  <div className={`font-medium ${selectedCategory === 'ChangeRequest' ? 'text-blue-700 dark:text-blue-400' : 'text-[var(--text-primary)]'}`}>
                    {t('adminIncidentDetail.changeRequest', 'Change request')}
                  </div>
                  <div className="text-sm text-[var(--text-secondary)]">
                    {t('adminIncidentDetail.changeRequestDesc', 'Feature request or modification')}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="reasoning" className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            {t('adminIncidentDetail.reasoning', 'Reasoning')}
          </label>
          <textarea id="reasoning" rows={4} value={reasoning} onChange={(e) => setReasoning(e.target.value)}
            placeholder={t('adminIncidentDetail.reasoningPlaceholder', 'Explain why this categorization was chosen...')}
            className="w-full px-3 py-2 border border-[var(--border-subtle)] rounded-lg bg-[var(--surface-base)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"
          />
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={backToProject}
            className="px-5 py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-sm font-medium transition-colors">
            {t('adminIncidentDetail.cancel', 'Cancel')}
          </button>
          <button onClick={handleSubmit} disabled={!selectedCategory || !reasoning.trim() || submitting}
            className="px-5 py-2.5 bg-[var(--accent-primary)] text-[var(--accent-text)] rounded-lg hover:bg-[var(--accent-secondary)] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors">
            {submitting ? t('adminIncidentDetail.submitting', 'Submitting...') : t('adminIncidentDetail.submit', 'Submit categorization')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminIncidentDetailPage;
